import { IComponent, ICustomEventData } from '@modules/src/interfaces/IComponents'
import { ContainersNames } from '../../containers/ContainersNames'
import { AssetType } from '@modules/src/enums/AssetType'
import { Orientation } from '@modules/src/enums/Orientation'
import GameSignals from '../../signals/GameSignals'
import { ComponentEvent } from '@modules/src/enums/ComponentEvent'
import { BaseContainer } from '@base/base/BaseContainer'
import { SpineAnimation } from '@base/spineAnimation/SpineAnimation'
import Facade from '@/Facade'
import gsap from 'gsap'
import { ModuleSignals } from '@modules/src/signals/ModuleSignals'
import * as PIXI from 'pixi.js'
import { DeviceUtils } from '@modules/src/utils/DeviceUtils'
import {
    isPortrait,
    isFreeSpins,
    getBaseBackgroundAnimation,
    getFreeSpinsBackgroundAnimation,
    ensureFreeSpinsTopInfoTexts,
    clearFreeSpinsTopTweens,
    updateFreeSpinsTopInfoTexts,
    onFsWinFrameTopShowFreeSpins,
    handleFsTopValuesFlow,
    handleFsTopMultiplierCollectFly,
    handleFsWinFrameTopOnBaseGame,
} from '@/managers/ui/BackgroundManager'
import { applyStaticBackgroundTint } from '@/utils/StaticBackgroundDim'

const MOBILE_LANDSCAPE_MACHINE_FRAME_X = 430.5
const FEATURE_BACKGROUND_CROSSFADE_DURATION_SEC = 2
let hasPlayedBaseIntroBackgroundSequence = false
let backgroundMode: 'base' | 'feature' = 'base'
let baseBackgroundSpineRef: SpineAnimation | null = null
let featureBackgroundSpineRef: SpineAnimation | null = null

const stopBackgroundCrossfadeTweens = () => {
    if (baseBackgroundSpineRef) {
        gsap.killTweensOf(baseBackgroundSpineRef)
    }

    if (featureBackgroundSpineRef) {
        gsap.killTweensOf(featureBackgroundSpineRef)
    }
}

const prepareFeatureBackground = () => {
    if (!featureBackgroundSpineRef) {
        return
    }

    try {
        featureBackgroundSpineRef.start([getFreeSpinsBackgroundAnimation()], [true], 0, [1])
    } catch {
        // Keep flow resilient if animation state is temporarily unavailable.
    }

    featureBackgroundSpineRef.visible = true
}

const runBaseToFeatureCrossfade = (sourceSignal: string) => {
    setBackgroundModeFeature()
    prepareFeatureBackground()

    const base = baseBackgroundSpineRef
    const feature = featureBackgroundSpineRef

    // Base spine handlers run before Bonus spine in the component tree. If feature is not
    // ready yet, keep base visible — the Bonus handler will re-enter once the ref is set.
    // Never hide base with no feature underneath: the Pixi canvas is transparent and the
    // HTML shell is black, which is what QA sees on Buy Bonus → Congrats.
    if (!feature) {
        logBackgroundDebug('BackgroundCrossfade:deferNoFeature', undefined, {
            sourceSignal,
            hasBase: Boolean(base),
        })
        return
    }

    stopBackgroundCrossfadeTweens()

    // Gapless crossfade: feature fully opaque underneath, then fade base out.
    // Fading both at once leaves the transparent canvas showing through as black.
    feature.visible = true
    feature.alpha = 1

    const hasVisibleBase = Boolean(base && base.visible && base.alpha > 0.01)

    if (!hasVisibleBase) {
        if (base) {
            base.stop()
            base.visible = false
            base.alpha = 0
        }

        logBackgroundDebug('BackgroundCrossfade:skipBaseFade', undefined, {
            sourceSignal,
            hasVisibleBase,
        })

        return
    }

    gsap.to(base, {
        duration: FEATURE_BACKGROUND_CROSSFADE_DURATION_SEC,
        alpha: 0,
        onComplete: () => {
            base.stop()
            base.visible = false
        },
    })

    logBackgroundDebug('BackgroundCrossfade:baseToFeature', undefined, {
        sourceSignal,
        hasVisibleBase,
    })
}

const isHoldAndWinFeatureActive = (): boolean =>
    Boolean(Facade.model?.spinState?.isHoldAndWinActive || Facade.model?.isBonusGameNow)

const isFeatureBackgroundMode = (): boolean => isFreeSpins() || isHoldAndWinFeatureActive()

const setBackgroundModeBase = () => {
    backgroundMode = 'base'
}

const setBackgroundModeFeature = () => {
    backgroundMode = 'feature'
}

const isBackgroundModeFeature = (): boolean => {
    if (backgroundMode === 'feature') {
        return true
    }

    return isFeatureBackgroundMode()
}

const logBackgroundDebug = (tag: string, data?: ICustomEventData, extra?: Record<string, unknown>) => {
    console.log('[BGDBG]', tag, {
        signal: (data as any)?.signalName,
        backgroundMode,
        isBackgroundModeFeature: isBackgroundModeFeature(),
        isFreeSpins: isFreeSpins(),
        isHoldAndWinActive: Boolean(Facade.model?.spinState?.isHoldAndWinActive),
        isBonusGameNow: Boolean(Facade.model?.isBonusGameNow),
        ...extra,
    })
}

const applyMobileLandscapeMachineFrameX = (container?: BaseContainer) => {
    if (!container) {
        return
    }

    if (!isPortrait() && DeviceUtils.isMobileDevice()) {
        container.x = MOBILE_LANDSCAPE_MACHINE_FRAME_X
    }
}

const getBaseIntroBackgroundAnimationSequence = (): string[] => {
    return isPortrait()
        ? ['p_free_in', 'p_free_loop', 'p_free_out', 'p_main']
        : ['l_free_in', 'l_free_loop', 'l_free_out', 'l_main']
}

export const BACKGROUND_COMPONENT: IComponent = {
    name: ContainersNames.BACKGROUND_CONTAINER,
    type: AssetType.CONTAINER,
    visible: false,
    events: [
        {
            onGameSignalsTriggered: [GameSignals.splashScreenButtonClicked],
            event: ComponentEvent.CUSTOM,
            callback: function (componentData: IComponent, container: BaseContainer) {
                container.alpha = 0
                container.visible = true
                gsap.to(container, {
                    duration: 2,
                    alpha: 1,
                })
            },
        },
    ],

    items: [
        {
            name: 'Static Base Background Landscape',
            type: AssetType.SPRITE,
            visible: false,
            src: 'bg_landscape.png',
            events: [
                {
                    onGameSignalsTriggered: [
                        GameSignals.showBaseGameBackground,
                        GameSignals.completeSplashScreenState,
                        ModuleSignals.gameResized,
                    ],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const signalName = (data as any)?.signalName
                        if (signalName === GameSignals.showBaseGameBackground || signalName === GameSignals.completeSplashScreenState) {
                            setBackgroundModeBase()
                        }

                        data.component.componentElement.visible = !isPortrait() && !isBackgroundModeFeature()
                        applyStaticBackgroundTint(data.component.componentElement)
                        logBackgroundDebug('StaticBaseLandscape:update', data, {
                            visible: data.component.componentElement.visible,
                            isPortrait: isPortrait(),
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        setBackgroundModeFeature()
                        data.component.componentElement.visible = false
                        logBackgroundDebug('StaticBaseLandscape:hideOnFeature', data, {
                            visible: data.component.componentElement.visible,
                        })
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0,
                    y: 0,
                },
            ],
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.75,
                    y: 0.75,
                },
            ],
        },
        {
            name: 'Static Base Background Portrait',
            type: AssetType.SPRITE,
            visible: false,
            src: 'bg_portrait.png',
            events: [
                {
                    onGameSignalsTriggered: [
                        GameSignals.showBaseGameBackground,
                        GameSignals.completeSplashScreenState,
                        ModuleSignals.gameResized,
                    ],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const signalName = (data as any)?.signalName
                        if (signalName === GameSignals.showBaseGameBackground || signalName === GameSignals.completeSplashScreenState) {
                            setBackgroundModeBase()
                        }

                        data.component.componentElement.visible = isPortrait() && !isBackgroundModeFeature()
                        applyStaticBackgroundTint(data.component.componentElement)
                        logBackgroundDebug('StaticBasePortrait:update', data, {
                            visible: data.component.componentElement.visible,
                            isPortrait: isPortrait(),
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        setBackgroundModeFeature()
                        data.component.componentElement.visible = false
                        logBackgroundDebug('StaticBasePortrait:hideOnFeature', data, {
                            visible: data.component.componentElement.visible,
                        })
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.PORTRAIT,
                    x: -2.4,
                    y: 4,
                },
            ],
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0.75,
                    y: 0.75,
                },
            ],
        },
        {
            name: 'Static Free Spins Background Landscape',
            type: AssetType.SPRITE,
            visible: false,
            src: 'bg_fs_landscape.png',
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground, ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const signalName = (data as any)?.signalName
                        if (signalName === GameSignals.showFreeSpinsBackground) {
                            setBackgroundModeFeature()
                        }

                        data.component.componentElement.visible = !isPortrait() && isBackgroundModeFeature()
                        applyStaticBackgroundTint(data.component.componentElement)
                        logBackgroundDebug('StaticFeatureLandscape:update', data, {
                            visible: data.component.componentElement.visible,
                            isPortrait: isPortrait(),
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground, GameSignals.completeSplashScreenState],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        setBackgroundModeBase()
                        data.component.componentElement.visible = false
                        logBackgroundDebug('StaticFeatureLandscape:hideOnBase', data, {
                            visible: data.component.componentElement.visible,
                        })
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0,
                    y: 0,
                },
            ],
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.75,
                    y: 0.75,
                },
            ],
        },
        {
            name: 'Static Free Spins Background Portrait',
            type: AssetType.SPRITE,
            visible: false,
            src: 'bg_fs_portrait.png',
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground, ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const signalName = (data as any)?.signalName
                        if (signalName === GameSignals.showFreeSpinsBackground) {
                            setBackgroundModeFeature()
                        }

                        data.component.componentElement.visible = isPortrait() && isBackgroundModeFeature()
                        applyStaticBackgroundTint(data.component.componentElement)
                        logBackgroundDebug('StaticFeaturePortrait:update', data, {
                            visible: data.component.componentElement.visible,
                            isPortrait: isPortrait(),
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground, GameSignals.completeSplashScreenState],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        setBackgroundModeBase()
                        data.component.componentElement.visible = false
                        logBackgroundDebug('StaticFeaturePortrait:hideOnBase', data, {
                            visible: data.component.componentElement.visible,
                        })
                    },
                },
            ],
            position: [
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0,
                    y: -2,
                },
            ],
            anchor: 0.5,
            scale: [
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1,
                    y: 1,
                },
            ],
        },
        {
            name: 'Base background',
            type: AssetType.SPINE,
            src: 'backgroundAnimation',
            visible: true,
            animationSettings: {
                default: {
                    animNames: ['l_main'],
                    loops: [true],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement as SpineAnimation
                        baseBackgroundSpineRef = element
                        runBaseToFeatureCrossfade(String((data as any)?.signalName || 'showFreeSpinsBackground'))
                        logBackgroundDebug('BaseSpine:hideOnFs', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).holdAndWinTransitionStart],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement as SpineAnimation
                        baseBackgroundSpineRef = element
                        runBaseToFeatureCrossfade(String((data as any)?.signalName || 'holdAndWinTransitionStart'))
                        logBackgroundDebug('BaseSpine:hideOnHoldAndWinStart', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroRequested],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (data.signalData?.kind !== 'holdandwin') {
                            return
                        }

                        const element = data.component.componentElement as SpineAnimation
                        baseBackgroundSpineRef = element
                        runBaseToFeatureCrossfade('featureIntroRequested:holdandwin')
                        logBackgroundDebug('BaseSpine:hideOnHoldAndWinStart', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.completeTransitionAnimation],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (hasPlayedBaseIntroBackgroundSequence || isBackgroundModeFeature()) {
                            logBackgroundDebug('BaseSpine:skipIntroSequence', data, {
                                hasPlayedBaseIntroBackgroundSequence,
                            })
                            return
                        }

                        hasPlayedBaseIntroBackgroundSequence = true
                        data.component.playAnimation({
                            animNames: getBaseIntroBackgroundAnimationSequence(),
                            loops: [false, false, false, true],
                            track: 0,
                            timeScales: [1, 1, 1, 1],
                        })
                        data.component.componentElement.visible = true
                        logBackgroundDebug('BaseSpine:playIntroSequence', data, {
                            visible: data.component.componentElement.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement as SpineAnimation
                        baseBackgroundSpineRef = element
                        stopBackgroundCrossfadeTweens()
                        setBackgroundModeBase()
                        data.component.playAnimation({
                            animNames: [getBaseBackgroundAnimation()],
                            loops: [true],
                            track: 0,
                            timeScales: [1],
                        })

                        element.visible = true
                        element.alpha = 1
                        logBackgroundDebug('BaseSpine:showOnBaseSignal', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).holdAndWinStop],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement as SpineAnimation
                        baseBackgroundSpineRef = element
                        stopBackgroundCrossfadeTweens()

                        if (isFreeSpins()) {
                            // During FS, post-H&W returns to feature visuals.
                            setBackgroundModeFeature()
                            element.stop()
                            element.visible = false
                            element.alpha = 0
                            logBackgroundDebug('BaseSpine:hideOnHoldAndWinStop:feature', data, {
                                visible: element.visible,
                            })
                            return
                        }

                        setBackgroundModeBase()
                        data.component.playAnimation({
                            animNames: [getBaseBackgroundAnimation()],
                            loops: [true],
                            track: 0,
                            timeScales: [1],
                        })
                        element.visible = true
                        element.alpha = 1
                        logBackgroundDebug('BaseSpine:showOnHoldAndWinStop:base', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (!data.component.componentElement.visible) return
                        if (isBackgroundModeFeature()) return

                        data.component.playAnimation({
                            animNames: [getBaseBackgroundAnimation()],
                            loops: [true],
                            track: 0,
                            timeScales: [1],
                        })
                        logBackgroundDebug('BaseSpine:refreshOnResize', data, {
                            visible: data.component.componentElement.visible,
                        })
                    },
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 1,
                    y: 1,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1,
                    y: 1,
                },
            ],
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: -75.5,
                    y: -5.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: -44.5,
                    y: -0.5,
                },
            ],
        },
        {
            name: 'Bonus background',
            type: AssetType.SPINE,
            src: 'backgroundAnimation',
            visible: false,
            animationSettings: {
                default: {
                    animNames: ['l_free_loop'],
                    loops: [true],
                    track: 0,
                    timeScales: [1],
                },
            },
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement as SpineAnimation
                        featureBackgroundSpineRef = element
                        // Re-enter crossfade after the Base handler may have deferred (no feature ref yet).
                        runBaseToFeatureCrossfade(String((data as any)?.signalName || 'showFreeSpinsBackground'))
                        logBackgroundDebug('FeatureSpine:showOnFs', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).holdAndWinTransitionStart],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement as SpineAnimation
                        featureBackgroundSpineRef = element
                        runBaseToFeatureCrossfade(String((data as any)?.signalName || 'holdAndWinTransitionStart'))
                        logBackgroundDebug('FeatureSpine:showOnHoldAndWinStart', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).featureIntroRequested],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (data.signalData?.kind !== 'holdandwin') {
                            return
                        }

                        const element = data.component.componentElement as SpineAnimation
                        featureBackgroundSpineRef = element
                        runBaseToFeatureCrossfade('featureIntroRequested:holdandwin')
                        logBackgroundDebug('FeatureSpine:showOnHoldAndWinStart', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement as SpineAnimation
                        featureBackgroundSpineRef = element
                        stopBackgroundCrossfadeTweens()
                        setBackgroundModeBase()
                        element.stop()
                        element.visible = false
                        element.alpha = 0
                        logBackgroundDebug('FeatureSpine:hideOnBase', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [(GameSignals as any).holdAndWinStop],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement as SpineAnimation
                        featureBackgroundSpineRef = element
                        stopBackgroundCrossfadeTweens()

                        if (isFreeSpins()) {
                            // During FS, post-H&W returns to feature visuals.
                            setBackgroundModeFeature()
                            data.component.playAnimation({
                                animNames: [getFreeSpinsBackgroundAnimation()],
                                loops: [true],
                                track: 0,
                                timeScales: [1],
                            })
                            element.visible = true
                            element.alpha = 1
                            logBackgroundDebug('FeatureSpine:showOnHoldAndWinStop:feature', data, {
                                visible: element.visible,
                            })
                            return
                        }

                        setBackgroundModeBase()
                        element.stop()
                        element.visible = false
                        element.alpha = 0
                        logBackgroundDebug('FeatureSpine:hideOnHoldAndWinStop:base', data, {
                            visible: element.visible,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (!data.component.componentElement.visible) return
                        if (!isBackgroundModeFeature()) return

                        data.component.playAnimation({
                            animNames: [getFreeSpinsBackgroundAnimation()],
                            loops: [true],
                            track: 0,
                            timeScales: [1],
                        })
                        logBackgroundDebug('FeatureSpine:refreshOnResize', data, {
                            visible: data.component.componentElement.visible,
                        })
                    },
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 1,
                    y: 1,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 1,
                    y: 1,
                },
            ],
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: -75.5,
                    y: -5.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: -44.5,
                    y: -0.5,
                },
            ],
        },
        {
            name: 'FS Win Frame Top',
            type: AssetType.SPRITE,
            visible: false,
            src: `FSWinFramepng.png`,
            events: [
                {
                    onGameSignalsTriggered: [GameSignals.showFreeSpinsBackground, ModuleSignals.gameResized],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        onFsWinFrameTopShowFreeSpins(data.component.componentElement as PIXI.Container)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.completeTransitionAnimation],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        if (isPortrait() || !isFreeSpins()) {
                            return
                        }

                        const element = data.component.componentElement
                        ensureFreeSpinsTopInfoTexts(element)
                        clearFreeSpinsTopTweens()
                        updateFreeSpinsTopInfoTexts(element)
                        gsap.killTweensOf(element)
                        element.alpha = 0
                        element.visible = true
                        gsap.to(element, {
                            duration: 0.35,
                            alpha: 1,
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.fsTopValuesFlow],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        handleFsTopValuesFlow(data)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.fsTopMultiplierCollectFly],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        handleFsTopMultiplierCollectFly(data)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.showBaseGameBackground],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        handleFsWinFrameTopOnBaseGame(data.component.componentElement as PIXI.Container)
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.bonusStop],
                    event: ComponentEvent.CUSTOM_NEW,
                    callback: function (data: ICustomEventData) {
                        const element = data.component.componentElement
                        gsap.killTweensOf(element)

                        if (!element.visible) {
                            return
                        }

                        gsap.to(element, {
                            duration: 0.35,
                            alpha: 0,
                            onComplete: () => {
                                element.visible = false
                            },
                        })
                    },
                },
                {
                    onGameSignalsTriggered: [GameSignals.completeSplashScreenState],
                    event: ComponentEvent.HIDE,
                },
            ],
            anchor: 0.5,
            position: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 697,
                    y: -19,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 540,
                    y: 220,
                },
            ],
            scale: [
                {
                    orientation: Orientation.LANDSCAPE,
                    x: 0.5,
                    y: 0.5,
                },
                {
                    orientation: Orientation.PORTRAIT,
                    x: 0,
                    y: 0,
                },
            ],
        },
    ],
}
